import { useEffect, useState } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input, Textarea } from '../../components/ui/Input';
import { Alert } from '../../components/ui/Alert';
import { useCreateCampaignAsset, useUpdateCampaignAsset } from '../../hooks/useCampaignAssets';
import { slugifyCampaign, type CampaignAsset, type SignalOption } from '../../api/campaignAssets';

interface CampaignAssetEditorProps {
  isOpen: boolean;
  onClose: () => void;
  /** Null when adding. */
  asset: CampaignAsset | null;
  signals: SignalOption[];
}

/** Add or edit a campaign asset. Admin-only, like the rest of the register. */
export function CampaignAssetEditor({ isOpen, onClose, asset, signals }: CampaignAssetEditorProps) {
  const create = useCreateCampaignAsset();
  const update = useUpdateCampaignAsset();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [signalTypes, setSignalTypes] = useState<string[]>([]);
  const [nurtureEligible, setNurtureEligible] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTitle(asset?.title ?? '');
    setSlug(asset?.slug ?? '');
    setSlugTouched(!!asset);
    setUrl(asset?.url ?? '');
    setDescription(asset?.description ?? '');
    setSignalTypes(asset?.signalTypes ?? []);
    setNurtureEligible(asset?.nurtureEligible ?? false);
  }, [isOpen, asset]);

  // The slug becomes utm_content, so it follows the title until someone edits it
  // by hand — at which point it stays put, because changing it later would break
  // the attribution of links already sitting in live sequences.
  const onTitleChange = (value: string) => {
    setTitle(value);
    if (!slugTouched) setSlug(slugifyCampaign(value));
  };

  const toggleSignal = (id: string) =>
    setSignalTypes((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  const submit = async () => {
    const payload = {
      title: title.trim(),
      slug: slug.trim(),
      url: url.trim(),
      description: description.trim() || null,
      signalTypes,
      nurtureEligible,
    };
    if (asset) await update.mutateAsync({ id: asset.id, ...payload });
    else await create.mutateAsync(payload);
    onClose();
  };

  const canSubmit = title.trim() && slug.trim() && url.trim() && !pending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={asset ? 'Edit asset' : 'Add asset'} size="lg">
      <div className="space-y-4 px-6 py-5">
        {error && <Alert variant="error">{error.message}</Alert>}

        <Input
          label="Title"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Your bid team's next hire might not be human"
        />

        <Input
          label="URL"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://sequesto.com/resources/..."
          hint="Where the prospect lands. A page we can measure beats a file download."
        />

        <Input
          label="Slug"
          value={slug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          placeholder="bid-team-next-hire"
          hint="Becomes utm_content. Changing it later breaks links already out in sequences."
        />

        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="One line on what it is and when to send it."
        />

        <div>
          <p className="mb-1.5 text-body-sm font-medium text-text-primary">Signals</p>
          <p className="mb-2 text-body-xs text-text-tertiary">
            Why the company is in the campaign. Leave all unticked if the asset suits any signal.
          </p>
          <div className="max-h-48 space-y-1 overflow-y-auto rounded-2xl border border-border-light p-3">
            {signals.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-body-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={signalTypes.includes(s.id)}
                  onChange={() => toggleSignal(s.id)}
                  className="rounded border-border"
                />
                {s.label}
                <span className="text-body-xs text-text-muted">· {s.weight}</span>
              </label>
            ))}
          </div>
        </div>

        <label className="flex items-start gap-2 text-body-sm text-text-secondary">
          <input
            type="checkbox"
            checked={nurtureEligible}
            onChange={(e) => setNurtureEligible(e.target.checked)}
            className="mt-1 rounded border-border"
          />
          <span>
            Also offer this as a nurture giveaway
            <span className="block text-body-xs text-text-tertiary">
              The nurture planner sends these unprompted to build trust, so only tick it for
              something genuinely useful on its own — not a comparison page or a pitch.
            </span>
          </span>
        </label>
      </div>

      <div className="flex justify-end gap-2 border-t border-border-light px-6 py-4">
        <Button variant="ghost" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!canSubmit} isLoading={pending}>
          {asset ? 'Save' : 'Add asset'}
        </Button>
      </div>
    </Modal>
  );
}
